import React from 'react';
import { Box, Container, Typography, Link as MuiLink, useTheme } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { useLocalizedPath } from '../../shared/hooks/useLocalizedPath';
import { stripLanguage } from '../../shared/constants/routes';
import { Citations } from './Citations';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';

const contentUseCase = new ContentUseCase();

export const Footer: React.FC = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const location = useLocation();
  const lp = useLocalizedPath();
  const socialMedia = contentUseCase.getSocialMediaContent(language);

  // Get citations to show based on current page
  const getCitationsForPage = (): string[] | undefined => {
    switch (stripLanguage(location.pathname).path) {
      case '/what-is-me':
        return undefined; // All citations for What is ME page
      default:
        return []; // No citations for other pages
    }
  };

  const citationsToShow = getCitationsForPage();

  return (
    <Box
      component="footer"
      sx={{
        mt: 'auto',
      }}
    >
      {citationsToShow && citationsToShow.length > 0 && <Citations citationNumbers={citationsToShow} />}
      {(!citationsToShow || citationsToShow.length === 0) && stripLanguage(location.pathname).path === '/what-is-me' && <Citations />}
      <Box
        sx={{
          py: theme.spacing(3),
          px: theme.spacing(2),
          backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: theme.spacing(2),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Box
              component="img"
              src="/icon.png"
              alt=""
              aria-hidden="true"
              sx={{ height: 20, width: 'auto' }}
            />
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              © {new Date().getFullYear()} ME SupportNet
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              columnGap: theme.spacing(2.5),
              rowGap: theme.spacing(1),
              flexWrap: 'wrap',
              justifyContent: { xs: 'center', sm: 'flex-end' },
              alignItems: 'center',
              typography: 'body2',
              whiteSpace: 'nowrap',
            }}
          >
            <MuiLink component={Link} to={lp('/impressum')} color="text.secondary" underline="hover" sx={{ textDecoration: 'none' }}>
              {language === 'de' ? 'Impressum' : 'Imprint'}
            </MuiLink>
            <MuiLink component={Link} to={lp('/privacy')} color="text.secondary" underline="hover" sx={{ textDecoration: 'none' }}>
              {language === 'de' ? 'Datenschutz' : 'Privacy'}
            </MuiLink>
            <MuiLink component={Link} to={lp('/cookie-settings')} color="text.secondary" underline="hover" sx={{ textDecoration: 'none' }}>
              {language === 'de' ? 'Cookie-Einstellungen' : 'Cookie Settings'}
            </MuiLink>
            <MuiLink component={Link} to={lp('/terms')} color="text.secondary" underline="hover" sx={{ textDecoration: 'none' }}>
              {language === 'de' ? 'AGB' : 'Terms'}
            </MuiLink>
            <MuiLink component={Link} to={lp('/account-deletion')} color="text.secondary" underline="hover" sx={{ textDecoration: 'none' }}>
              {language === 'de' ? 'Konto löschen' : 'Delete Account'}
            </MuiLink>
            <MuiLink component={Link} to={lp('/mitmachen')} color="text.secondary" underline="hover" sx={{ textDecoration: 'none' }}>
              {language === 'de' ? 'Kontakt' : 'Contact'}
            </MuiLink>
            <MuiLink
              href={socialMedia.instagram.url}
              target="_blank"
              rel="noopener noreferrer"
              color="text.secondary"
              underline="hover"
              sx={{ textDecoration: 'none' }}
            >
              Instagram {socialMedia.instagram.handle}
            </MuiLink>
          </Box>
        </Box>
      </Container>
      </Box>
    </Box>
  );
};

